import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ElMessage, ElNotification } from 'element-plus';
import { userStore } from '@/stores/user';
import { getUuid } from '@/utils/tools';
import mockJson from './mock';

/** 取消重复下载请求的工具逻辑 */
const workingDownloadRequests: Record<string, AbortController> = {};
let downloadController: AbortController | null = null;

// 生成下载请求唯一标识（避免重复请求）
const getDownloadReqKey = (config: InternalAxiosRequestConfig) => {
  const requestData = config.data ? JSON.stringify(config.data) : '';
  return `${config.url}_${config.method}_${requestData}`;
};

// 取消重复的下载请求
const cancelDuplicateDownload = (reqKey: string) => {
  if (workingDownloadRequests[reqKey]) {
    workingDownloadRequests[reqKey].abort();
    delete workingDownloadRequests[reqKey];
    downloadController = null;
  }
};

/**
 * 专门用于下载的 Axios 实例
 * 特性：
 * 1. 默认响应类型为 blob（二进制流）
 * 2. 强制返回完整响应对象（包含 headers，方便提取文件名）
 * 3. 保留重复请求取消、认证、Mock 等核心能力
 * 4. 仅处理下载相关异常，逻辑更简洁
 */
const downloadAxios = axios.create({
  responseType: 'blob', // 下载默认接收二进制流
  validateStatus(status: number) {
    // 下载场景特殊状态码处理（如 206 断点续传，根据实际需求调整）
    switch (status) {
      case 400:
        ElMessage.error('下载请求参数错误');
        return false;
      case 401:
        ElMessage.warning('登录已失效，请重新登录');
        userStore().reset();
        setTimeout(() => window.location.reload(), 1000);
        return false;
      case 403:
        ElMessage.warning('无下载权限');
        return false;
      case 404:
        ElMessage.warning('下载资源不存在');
        return false;
      case 500:
        ElMessage.warning('服务端错误，下载失败');
        return false;
      default:
        return status >= 200 && status < 300; // 正常响应范围
    }
  },
});

// 请求拦截器：仅处理下载相关配置
downloadAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 1. Mock 配置（开发/测试环境生效）
    if (config.mock && ['dev', 'test'].includes(import.meta.env.VITE_APP_ENV as string)) {
      config.adapter = customConfig => {
        return new Promise((resolve, reject) => {
          const mockData = mockJson[config.url ?? ''];
          if (mockData) {
            // 模拟下载二进制流（用空 Blob 占位，实际 Mock 需根据文件类型调整）
            setTimeout(() => {
              resolve({
                data: new Blob([JSON.stringify(mockData)]),
                status: 200,
                statusText: 'OK',
                headers: {
                  'content-disposition': 'attachment;filename=mock_download.zip', // 模拟文件名
                  'content-type': 'application/zip',
                },
                config: customConfig,
                request: {},
              });
            }, config.waiting ?? 300);
            return;
          }
          reject(new Error('下载 Mock 数据不存在'));
        });
      };
    }

    // 2. 统一请求格式（对齐原有实例的 post 数据结构）
    if (config.method === 'post' && !config.data?.args && config.headers['Content-Type'] !== 'multipart/form-data') {
      config.data = {
        args: config.data ?? {},
        header: {
          sysNo: 'emcomp-back-office',
          traceId: getUuid(),
          userMark: 'test',
        },
      };
    }

    // 3. 携带认证 Token
    config.headers['authorization'] = localStorage.getItem('token') || '';

    // 4. 取消重复下载请求
    const reqKey = getDownloadReqKey(config);
    cancelDuplicateDownload(reqKey);
    downloadController = new AbortController();
    config.signal = downloadController.signal;
    workingDownloadRequests[reqKey] = downloadController;

    return config;
  },
  error => {
    ElMessage.error('下载请求初始化失败');
    return Promise.reject(error);
  }
);

// 响应拦截器：仅处理下载相关逻辑，强制返回完整响应（含 headers）
downloadAxios.interceptors.response.use(
  (response: AxiosResponse) => {
    // 1. 处理下载错误（如后端返回 JSON 格式的错误信息）
    const contentType = response.headers['content-type'] || response.headers['Content-Type'];
    if (contentType?.includes('application/json')) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsText(response.data, 'utf-8');
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result as string);
            ElNotification.warning({
              title: '下载失败',
              message: errorData.message || '服务端返回错误',
              dangerouslyUseHTMLString: true,
            });
            reject(errorData);
          } catch (e) {
            ElMessage.error('下载响应解析失败');
            reject(e);
          }
        };
      });
    }

    // 2. 正常下载响应：直接返回完整对象（含 headers 和 blob 数据）
    return response;
  },
  (err: AxiosError) => {
    // 处理网络错误或取消请求
    if (err.name === 'CanceledError') {
      ElMessage.info('已取消下载');
    } else {
      ElMessage.error(err.message || '下载网络异常');
    }
    return Promise.reject(err);
  }
);

export default downloadAxios;
